import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles, CheckCircle2 } from "lucide-react";
import { TspBreadcrumb } from "../../components/primitives";
import TspLoadError from "../../components/common/TspLoadError";
import TspMasterDataCard from "../../components/patterns/TspMasterDataCard";
import TspApiService from "../../api/tspApiService";
import useTspApiResource from "../../hooks/useTspApiResource";
import useTspLocationResourceData from "../../hooks/useTspLocationResourceData";
import useTspPermission from "../../hooks/useTspPermission";
import TSP_PERMISSIONS from "../../constants/tspPermissions";
import TspBackButton from "../../components/common/TspBackButton";

const EMPTY_DRAFT = { state_id: "", city_name: "", stay_name: "", contact: "", address: "", charges: "", capacity: "" };

export default function TspAccommodationPage() {
  const { hasPermission } = useTspPermission();
  const canManage = hasPermission(TSP_PERMISSIONS.ACCOMMODATION_MANAGE);
  const location = useLocation();

  const states = useTspApiResource(() => TspApiService.listStates(), []);
  const { loading, error, reload, records, add, remove } = useTspLocationResourceData(TspApiService.accommodations);

  // Parse URL query parameters (e.g. from Journey Detail -> Add Accommodation button)
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlJourneyId = searchParams.get("journey") || "";
  const urlLocationId = searchParams.get("location") || "";
  const urlStateId = searchParams.get("state_id") || "";
  const urlStateName = searchParams.get("state") || "";
  const urlCityName = searchParams.get("city") || searchParams.get("city_name") || "";

  const [selectedJourneyId, setSelectedJourneyId] = useState(urlJourneyId);
  const [selectedStopId, setSelectedStopId] = useState(urlLocationId);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [autoFilledInfo, setAutoFilledInfo] = useState(null);

  // Safe journeys list (falls back safely if test mock doesn't include listJourneys)
  const journeys = useTspApiResource(
    () =>
      typeof TspApiService.listJourneys === "function"
        ? TspApiService.listJourneys({ limit: 100 }).catch(() => ({ data: [] }))
        : Promise.resolve({ data: [] }),
    []
  );

  // Safe route for selected journey
  const route = useTspApiResource(
    () =>
      selectedJourneyId && typeof TspApiService.getRoute === "function"
        ? TspApiService.getRoute(selectedJourneyId).catch(() => ({ data: null }))
        : Promise.resolve({ data: null }),
    [selectedJourneyId]
  );

  // Auto-fill from URL parameters on first mount or when states load
  useEffect(() => {
    if (!states.data) return;

    let targetStateId = urlStateId;
    if (!targetStateId && urlStateName) {
      const match = states.data.find(
        (s) =>
          s.name.toLowerCase() === urlStateName.toLowerCase() ||
          (s.code && s.code.toLowerCase() === urlStateName.toLowerCase())
      );
      if (match) targetStateId = String(match.id);
    }

    if (targetStateId || urlCityName) {
      setDraft((prev) => ({
        ...prev,
        state_id: targetStateId ? String(targetStateId) : prev.state_id,
        city_name: urlCityName || prev.city_name,
      }));

      const stateName = targetStateId ? states.data.find((s) => String(s.id) === String(targetStateId))?.name : urlStateName;
      if (urlCityName || stateName) {
        setAutoFilledInfo({ cityName: urlCityName, stateName });
      }
    }
  }, [states.data, urlStateId, urlStateName, urlCityName]);

  // When route data loads for selectedJourneyId (e.g. from URL or dropdown)
  useEffect(() => {
    if (!route.data?.cities || !states.data) return;
    const cities = route.data.cities;

    // Try to find the matching stop by selectedStopId, urlLocationId, or urlCityName
    const stop = cities.find(
      (c) =>
        (selectedStopId && (String(c.location_id) === String(selectedStopId) || String(c.id) === String(selectedStopId))) ||
        (urlLocationId && (String(c.location_id) === String(urlLocationId) || String(c.id) === String(urlLocationId))) ||
        (urlCityName && c.city_name?.toLowerCase() === urlCityName.toLowerCase())
    );

    if (stop) {
      const labelParts = (stop.label || "").split(",");
      const cityName = stop.city_name || (labelParts[0] ? labelParts[0].trim() : "");
      let stateId = stop.state_id ? String(stop.state_id) : "";
      const stateName = stop.state_name || (labelParts[1] ? labelParts[1].trim() : "");

      if (!stateId && stateName) {
        const match = states.data.find((s) => s.name.toLowerCase() === stateName.toLowerCase());
        if (match) stateId = String(match.id);
      }

      setDraft((prev) => ({
        ...prev,
        state_id: stateId || prev.state_id,
        city_name: cityName || prev.city_name,
      }));
      setSelectedStopId(String(stop.location_id || stop.id));
      setAutoFilledInfo({ cityName, stateName });
    }
  }, [route.data, states.data, selectedStopId, urlLocationId, urlCityName]);

  // Handler when user picks a route stop manually from dropdown
  const handleSelectRouteStop = (stopId) => {
    if (!stopId || !route.data?.cities || !states.data) {
      setAutoFilledInfo(null);
      return;
    }
    const stop = route.data.cities.find((c) => String(c.location_id || c.id) === String(stopId));
    if (!stop) return;

    const labelParts = (stop.label || "").split(",");
    const cityName = stop.city_name || (labelParts[0] ? labelParts[0].trim() : "");
    let stateId = stop.state_id ? String(stop.state_id) : "";
    const stateName = stop.state_name || (labelParts[1] ? labelParts[1].trim() : "");

    if (!stateId && stateName) {
      const match = states.data.find((s) => s.name.toLowerCase() === stateName.toLowerCase());
      if (match) stateId = String(match.id);
    }

    setDraft((prev) => ({
      ...prev,
      state_id: stateId || prev.state_id,
      city_name: cityName || prev.city_name,
    }));
    setAutoFilledInfo({ cityName, stateName });
  };

  if (loading) return <div className="tsp-page tsp-dashboard-loading">Loading accommodation…</div>;
  if (error) return <TspLoadError error={error} onRetry={reload} what="accommodation records" />;

  const columns = [
    { key: "location", label: "City", render: (row) => row.location?.label || "—" },
    { key: "stay_name", label: "Stay Name" },
    { key: "contact", label: "Contact", render: (row) => row.contact || "—" },
    { key: "address", label: "Address", render: (row) => row.address || "—" },
    { key: "charges", label: "Charges", render: (row) => (row.charges == null ? "Free" : `₹${row.charges}`) },
    { key: "capacity", label: "Capacity", render: (row) => (row.capacity == null ? "—" : `${row.capacity} persons`) },
  ];

  const fields = [
    { key: "state_id", label: "State", type: "select", options: (states.data || []).map((s) => ({ value: String(s.id), label: s.name })), required: true },
    { key: "city_name", label: "City", required: true, placeholder: "City" },
    { key: "stay_name", label: "Stay Name", required: true, placeholder: "Stay Name" },
    { key: "contact", label: "Contact", placeholder: "Contact Person / Phone" },
    { key: "address", label: "Address", placeholder: "Full Address" },
    { key: "charges", label: "Charges", placeholder: "Charges" },
    { key: "capacity", label: "Capacity", placeholder: "Capacity" },
  ];

  const handleAdd = (submittedDraft) => {
    const body = {
      state_id: Number(submittedDraft.state_id),
      city_name: submittedDraft.city_name.trim(),
      stay_name: submittedDraft.stay_name.trim(),
    };
    // The journey chosen above (or the one the page was opened from): the stay is
    // recorded on that journey, so it shows in that journey's and temple's stats.
    if (selectedJourneyId) body.journey_id = Number(selectedJourneyId);
    if (submittedDraft.contact?.trim()) body.contact = submittedDraft.contact.trim();
    if (submittedDraft.address?.trim()) body.address = submittedDraft.address.trim();
    if (submittedDraft.charges !== "" && submittedDraft.charges != null) body.charges = Number(submittedDraft.charges);
    if (submittedDraft.capacity !== "" && submittedDraft.capacity != null) body.capacity = Number(submittedDraft.capacity);
    return add(body);
  };

  const topHelper = (
    <div className="tsp-route-prefill-bar">
      <div className="tsp-route-prefill-header">
        <Sparkles size={16} className="tsp-prefill-icon" />
        <span className="tsp-prefill-title">Prefill from Journey Route Stop</span>
      </div>
      <div className="tsp-route-prefill-controls">
        <div className="tsp-prefill-field">
          <label htmlFor="prefill-journey">Select Journey</label>
          <select
            id="prefill-journey"
            className="tsp-select tsp-select-block"
            value={selectedJourneyId}
            onChange={(e) => {
              setSelectedJourneyId(e.target.value);
              setSelectedStopId("");
              setAutoFilledInfo(null);
            }}
          >
            <option value="">-- Choose Journey (Optional) --</option>
            {(journeys.data || []).map((j) => (
              <option key={j.id} value={j.id}>
                {j.journey_name || `Journey #${j.id}`} ({j.status})
              </option>
            ))}
          </select>
        </div>

        <div className="tsp-prefill-field">
          <label htmlFor="prefill-stop">Select Route City / Stop</label>
          <select
            id="prefill-stop"
            className="tsp-select tsp-select-block"
            value={selectedStopId}
            disabled={!selectedJourneyId || route.loading}
            onChange={(e) => {
              const stopId = e.target.value;
              setSelectedStopId(stopId);
              handleSelectRouteStop(stopId);
            }}
          >
            <option value="">{route.loading ? "Loading stops…" : "-- Choose City Stop --"}</option>
            {(route.data?.cities || []).map((c) => (
              <option key={c.location_id || c.id} value={c.location_id || c.id}>
                {c.sequence_number ? `Stop #${c.sequence_number}: ` : ""}{c.label || c.city_name}
              </option>
            ))}
          </select>
        </div>

        {autoFilledInfo && (
          <div className="tsp-prefill-badge">
            <CheckCircle2 size={15} />
            <span>
              Auto-filled: <strong>{autoFilledInfo.cityName}</strong>{autoFilledInfo.stateName ? `, ${autoFilledInfo.stateName}` : ""}
            </span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="tsp-page">
      <div className="tsp-page-heading-row">
        <div>
          <h1>Accommodation</h1>
          <TspBreadcrumb items={["Dashboard", "Accommodation"]} />
        </div>
        <TspBackButton fallback="/tsp" />
      </div>
      <TspMasterDataCard
        title="Accommodation"
        formTitle="Add New Accommodation / Ashram Stay"
        formSubtitle="Register devotee ashrams, guesthouses, and dormitories with stay capacity and facilities."
        columns={columns}
        fields={fields}
        records={records}
        emptyDraft={EMPTY_DRAFT}
        draft={draft}
        onDraftChange={setDraft}
        canManage={canManage}
        onAdd={handleAdd}
        onDelete={remove}
        rowLabel={(row) => row.stay_name}
        topHelper={topHelper}
      />
    </div>
  );
}

